import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type {
  EstructuraPlan,
  PlanInversionEstructuraItem,
  PlanTrabajoEstructuraItem,
  CronogramaEstructuraItem,
  SolucionesTecnicasItem
} from '../../services/negocio.service';

export interface PlanNegocioPdfMeta {
  titulo?: string | null;
  productor?: string | null;
  rut?: string | null;
  unidad_operativa?: string | null;
  estado?: string | null;
  fecha_emision?: string | null;
}

@Component({
  selector: 'app-plan-negocio-pdf',
  templateUrl: './plan-negocio-pdf.component.html',
  styleUrls: ['./plan-negocio-pdf.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanNegocioPdfComponent {
  @Input() estructura: EstructuraPlan | null = null;
  @Input() meta: PlanNegocioPdfMeta | null = null;
  @Input() logoSrc: string = 'assets/logo-indap.png';

  get ahora(): string {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy} a las ${hh}:${min}hrs`;
  }

  get textoDescargado(): string {
    return `Descargado el ${this.ahora}`;
  }

  get solucionesTecnicasAgrupadas(): Array<{
    rubro: string;
    areas: Array<{ area: string; items: SolucionesTecnicasItem[] }>;
  }> {
    const items = this.estructura?.soluciones_tecnicas ?? [];
    const safe = Array.isArray(items) ? items : [];
    const byRubro = new Map<string, SolucionesTecnicasItem[]>();
    for (const it of safe) {
      const rubro = String(it?.rubro ?? '').trim() || 'Sin rubro';
      const prev = byRubro.get(rubro);
      if (prev) prev.push(it);
      else byRubro.set(rubro, [it]);
    }
    const rubrosOrdenados = Array.from(byRubro.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], 'es')
    );
    return rubrosOrdenados.map(([rubro, arr]) => {
      const byArea = new Map<string, SolucionesTecnicasItem[]>();
      for (const it of arr) {
        const area = String(it?.area_gestion ?? '').trim() || 'Sin área de gestión';
        const prev = byArea.get(area);
        if (prev) prev.push(it);
        else byArea.set(area, [it]);
      }
      const areasOrdenadas = Array.from(byArea.entries()).sort((a, b) =>
        a[0].localeCompare(b[0], 'es')
      );
      return {
        rubro,
        areas: areasOrdenadas.map(([area, items2]) => ({ area, items: items2 })),
      };
    });
  }

get ciclosAgrupados(): Array<{
  ciclo: number;
  plan: PlanTrabajoEstructuraItem[];
  cronograma: { rangoInicio: Date; meses: string[]; items: PlanTrabajoEstructuraItem[] }[];
}> {
  const planItems = Array.isArray(this.estructura?.plan_trabajo) ? this.estructura.plan_trabajo : [];
  const cronItems = Array.isArray(this.estructura?.cronograma) ? this.estructura.cronograma : [];

  const byCiclo = new Map<number, { plan: PlanTrabajoEstructuraItem[]; cronograma: CronogramaEstructuraItem[] }>();

  for (const it of planItems) {
    const ciclo = it?.ciclo ?? 0;
    const entry = byCiclo.get(ciclo) ?? { plan: [], cronograma: [] };
    entry.plan.push(it);
    byCiclo.set(ciclo, entry);
  }

  for (const it of cronItems) {
    const ciclo = it?.ciclo ?? 0;
    const entry = byCiclo.get(ciclo) ?? { plan: [], cronograma: [] };
    entry.cronograma.push(it);
    byCiclo.set(ciclo, entry);
  }

  return Array.from(byCiclo.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([ciclo, obj]) => {
      const bloques: { rangoInicio: Date; meses: string[]; items: PlanTrabajoEstructuraItem[] }[] = [];

      // ordenar todas las fechas del ciclo
      const todasFechas: Date[] = [];
      for (const it of obj.cronograma) {
        for (const f of it.fechas ?? []) {
          if (f.fecha_inicio) todasFechas.push(new Date(f.fecha_inicio));
          if (f.fecha_termino) todasFechas.push(new Date(f.fecha_termino));
        }
      }
      todasFechas.sort((a, b) => a.getTime() - b.getTime());

      if (todasFechas.length > 0) {
        let bloqueInicio = todasFechas[0];

        // mientras el inicio del bloque esté antes o igual a la última fecha
        while (bloqueInicio <= todasFechas[todasFechas.length - 1]) {
          const bloqueFin = new Date(bloqueInicio);
          bloqueFin.setMonth(bloqueFin.getMonth() + 12);

          const itemsBloque = obj.cronograma.filter(it =>
            (it.fechas ?? []).some(f => {
              const inicio = f.fecha_inicio ? new Date(f.fecha_inicio) : null;
              const termino = f.fecha_termino ? new Date(f.fecha_termino) : null;
              if (!inicio || !termino) return false;
              // condición: el rango del ítem se solapa con el bloque
              return inicio < bloqueFin && termino >= bloqueInicio;
            })
          );

          bloques.push({
            rangoInicio: new Date(bloqueInicio), // clonar para no mutar
            meses: this.generarMesesDesde(bloqueInicio),
            items: itemsBloque
          });

          // avanzar al siguiente bloque de 12 meses
          bloqueInicio = new Date(bloqueInicio);
          bloqueInicio.setMonth(bloqueInicio.getMonth() + 12);
        }
      }

      return { ciclo, plan: obj.plan, cronograma: bloques };
    });
}

// Meses estáticos
meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];



  get planInversionAgrupadoPorRubro(): Array<{ rubro: string; items: PlanInversionEstructuraItem[] }> {
    const items = this.estructura?.plan_inversion ?? [];
    const safe = Array.isArray(items) ? items : [];
    const byRubro = new Map<string, PlanInversionEstructuraItem[]>();
    for (const it of safe) {
      const rubro = String(it?.rubro ?? '').trim() || 'Sin rubro';
      const prev = byRubro.get(rubro);
      if (prev) prev.push(it);
      else byRubro.set(rubro, [it]);
    }
    return Array.from(byRubro.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'es'))
      .map(([rubro, arr]) => ({ rubro, items: arr }));
  }

  textoRangoFechas(inicio: string | null | undefined, termino: string | null | undefined): string {
    const a = this.formatearFechaIso(inicio);
    const b = this.formatearFechaIso(termino);
    if (a && b) return `${a} - ${b}`;
    return a || b || '-';
  }

  get hoyIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  textoCategoriasPda(raw: SolucionesTecnicasItem['categorias_pdas']): string {
    const arr = Array.isArray(raw) ? raw : [];
    const labels = arr
      .map((x) => String(x?.descripcion ?? '').trim())
      .filter(Boolean);
    return labels.length ? labels.map((t) => `- ${t}`).join('\n') : '-';
  }

  textoActividadesGrupales(raw: SolucionesTecnicasItem['asesoria_especializada']): string {
    const arr = Array.isArray(raw) ? raw : [];
    const labels = arr
      .map((x) => String(x?.nombre ?? '').trim())
      .filter(Boolean);
    return labels.length ? labels.map((t) => `- ${t}`).join('\n') : '-';
  }

  formatearFechaIso(fechaIso: string | null | undefined): string {
    const t = (fechaIso ?? '').trim();
    if (!t) return '';
    const datePart = t.split('T')[0] ?? t;
    const parts = datePart.split('-');
    if (parts.length !== 3) return datePart;
    const [y, m, d] = parts;
    if (!y || !m || !d) return datePart;
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }

  formatearFechaIsoHora(fechaIso: string | null | undefined): string {
  const t = (fechaIso ?? '').trim();
  if (!t) return '';

  // separar fecha y hora
  const [datePart, timePart] = t.split('T');

  // procesar fecha
  const parts = datePart.split('-');
  if (parts.length !== 3) return datePart;
  const [y, m, d] = parts;
  if (!y || !m || !d) return datePart;
  const fechaFormateada = `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;

  // procesar hora si existe
  if (!timePart) return fechaFormateada;

  // quitar zona horaria si viene (ej: +00:00 o Z)
  const horaCruda = timePart.split(/[Z+-]/)[0];
  const horaFormateada = horaCruda.length >= 8
    ? horaCruda.slice(0, 8) // HH:MM:SS
    : horaCruda;

  return `${fechaFormateada} ${horaFormateada}`;
}

  textoBool(v: boolean | null | undefined): string {
    if (v === true) return 'Sí';
    if (v === false) return 'No';
    return '';
  }

nombresMeses = [
    'Enero','Febrero','Marzo','Abril','Mayo','Junio',
    'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
  ];

estaEnMes(visita: CronogramaEstructuraItem, mes: string, year: number, meses: string[]): boolean {
  if (!visita.fechas || visita.fechas.length === 0) return false;

  const idxMes = meses.indexOf(mes);
  if (idxMes < 0) return false;

  // fecha representativa del mes de la celda
  const fechaMes = new Date(year, (this.obtenerIndiceMes(mes)), 1);

  return visita.fechas.some(f => {
    if (!f.fecha_inicio || !f.fecha_termino) return false;

    const inicio = new Date(f.fecha_inicio);
    const termino = new Date(f.fecha_termino);

    return (
      fechaMes >= new Date(inicio.getFullYear(), inicio.getMonth(), 1) &&
      fechaMes <= new Date(termino.getFullYear(), termino.getMonth(), 1)
    );
  });
}

// helper para mapear abreviaturas a índice real
private obtenerIndiceMes(abrev: string): number {
  const nombresMeses = [
    'Ene','Feb','Mar','Abr','May','Jun',
    'Jul','Ago','Sep','Oct','Nov','Dic'
  ];
  return nombresMeses.indexOf(abrev);
}


formatCLP(valor: string | number | null | undefined): string {
  if (!valor) return '-';

  valor = valor.toString()
  // Intentar convertir el string a número
  const numero = Number(valor);

  // Si no es un número válido, devolver el string original
  if (isNaN(numero)) return valor;

  // Formatear como pesos chilenos
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(numero);
}

formatCronograma(valor: string | null | undefined): string {
  if (!valor ) {
    return '-';
  }
  return `Año ${valor}`;
}

getVisitasAg(items: any[]): any[] {
  return items
    .filter(v => v.tipo === "AG")
    
}
getVisitasTg(items: any[]): any[] {
  return items
    .filter(v => v.tipo === "TG")
}
getVisitas(items: any[]): any[] {
  return items
    .filter(v => v.tipo === "AT")
}

truncateNombre(nombre: string, maxLength: number = 40): string {
  if (!nombre) return '-';
  return nombre.length > maxLength
    ? nombre.slice(0, maxLength) + '...'
    : nombre;
}

  /** Parsea YYYY-MM-DD como fecha local para evitar desfase de un día por UTC. */
  private parseFechaLocal(iso: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return null;
    const y = parseInt(match[1], 10);
    const m = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const d = new Date(y, m, day);
    return isNaN(d.getTime()) ? null : d;
  }

private parseFechaDesdeApi(iso: string): Date | null {
    const s = (iso ?? '').trim();
    if (!s) return null;
    const byDay = this.parseFechaLocal(s);
    if (byDay) return byDay;
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }


generarMesesDesde(fechaInicio: Date | null): string[] {

  const mesInicio = fechaInicio ? fechaInicio.getMonth() : 0; // si es null, empieza en enero
  const meses: string[] = [];
  for (let i = 0; i < 12; i++) {
    const nombreCompleto = this.nombresMeses[(mesInicio + i) % 12];
    // tomar solo las primeras 3 letras
    meses.push(nombreCompleto.substring(0, 3));
  }
  return meses;
}

obtenerPrimeraFecha(ciclo: any): Date | null {
  let min: Date | null = null;
  for (const s of ciclo){

    for (const f of s.fechas ?? []) {
      const inicio = this.parseFechaDesdeApi(f.fecha_inicio);
      const termino = this.parseFechaDesdeApi(f.fecha_termino);
      if (inicio && (!min || inicio < min)) min = inicio;
      if (termino && (!min || termino < min)) min = termino;
    }
  }
  return min;
}

obtenerYearParaMes(rangoInicio: Date, idxMes: number): number {
  const mesInicio = rangoInicio.getMonth(); // ej. 6 = Julio
  const yearInicio = rangoInicio.getFullYear();
  const mesReal = (mesInicio + idxMes) % 12;
  const yearOffset = Math.floor((mesInicio + idxMes) / 12);
  return yearInicio + yearOffset;
}

}